import { IHighValueUpcharge, updateHighValue } from "@/api/settings";
import CloseIcon from "@mui/icons-material/Close";

import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";
import { FormButton, FormContainer, FormTextFieldElement } from "@rhf-kit/mui";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";

interface Props {
    highValue: IHighValueUpcharge | undefined;
    open: boolean;
    setOpen: (open: boolean) => void;
    setSelected: (high_value: IHighValueUpcharge | undefined) => void;
    mutateHighValues: () => void;
}

interface FormData {
    minimumValue: number;
    upcharge: number;
}

export const UpdateHighValueModal = ({
    highValue,
    open,
    setOpen,
    mutateHighValues,
    setSelected,
}: Props) => {
    const updateHighValueUpcharge = useMutation({
        mutationFn: updateHighValue,
        onSuccess: () => {
            mutateHighValues();
            enqueueSnackbar("High Value successfully updated", {
                variant: "success",
            });
            setOpen(false);
            setSelected(undefined);
        },
        onError: (error: AxiosError<{ detail: string }>) => {
            if (
                error.response &&
                error.response.data &&
                error.response.data.detail
            ) {
                enqueueSnackbar(error.response.data.detail, {
                    variant: "error",
                });
            }
        },
    });

    if (!highValue) {
        return null;
    }

    const defaultValues = {
        minimumValue: parseFloat((highValue.minimum_value / 100).toFixed(2)),
        upcharge: parseFloat((highValue.upcharge / 100).toFixed(2)),
    };

    const handleSubmit = (data: FormData) => {
        updateHighValueUpcharge.mutate({
            id: highValue.id,
            minimum_value: data.minimumValue * 100,
            upcharge: data.upcharge * 100,
        });
    };

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={() => setOpen(false)}
        >
            <DialogTitle>
                <Toolbar variant="dense">
                    <Typography variant="h6" fontWeight={600}>
                        Edit high value upcharge
                    </Typography>
                    <Box flexGrow={1} />
                    <IconButton edge="end" onClick={() => setOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </DialogTitle>
            <DialogContent>
                <FormContainer
                    defaultValues={defaultValues}
                    onSuccess={handleSubmit}
                >
                    <Stack direction="column" spacing={2} my={2}>
                        <FormTextFieldElement
                            id="minimumValue"
                            name="minimumValue"
                            label="Minimum Value"
                            size="small"
                            required
                            rules={{
                                min: {
                                    value: 1,
                                    message:
                                        "Minimum Value must be at least $0.01",
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        variant="standard"
                                    >
                                        $
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormTextFieldElement
                            id="upcharge"
                            name="upcharge"
                            label="Upcharge"
                            type="number"
                            rules={{
                                min: {
                                    value: 1,
                                    message: "Upcharge must be at least $0.01",
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        variant="standard"
                                    >
                                        $
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                            required
                        />
                    </Stack>
                    <FormButton variant="contained" color="primary">
                        Edit High Value Upcharge
                    </FormButton>
                </FormContainer>
            </DialogContent>
        </Dialog>
    );
};
